import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useDateWindow } from '@/components/date-range-picker'
import { DetailList, useListExpansion } from '@/components/detail-list'
import { EmptyState } from '@/components/empty-state'
import { MetricCards } from '@/components/metric-cards'
import { RankedList } from '@/components/ranked-list'
import { SerpStar } from '@/components/serp-star'
import { Button } from '@/components/ui/button'
import { shortenUrl } from '@/lib/format'
import { useTrackedKeywords } from '@/lib/serp'
import {
  DETAIL_PAGE_SIZE,
  type KeywordDetail,
  useKeywordDetail,
  useKeywordPages,
  useKeywordRelated,
} from '@/lib/site-data'
import {
  BACK_LINK,
  DetailHeader,
  MetricsSkeleton,
  ShareOfSite,
  SiteSectionBody,
  useSiteScope,
} from '../-section'

export const Route = createFileRoute('/_app/$org/$host/keywords/$queryId')({
  component: KeywordDetailPage,
})

/**
 * One keyword, on its own page rather than inside an expanded row. A row that
 * unfolds can hold a list; it cannot hold a chart, and the question people
 * arrive with — "when did this start moving" — is a chart.
 */
function KeywordDetailPage() {
  const { org, host, range, site } = useSiteScope()
  const { queryId } = Route.useParams()
  const { data, isPending, error } = useKeywordDetail(site, range, Number(queryId))
  useDateWindow(data?.range)

  const tracked = useTrackedKeywords(site)
  const starred = tracked.data?.rows.some((row) => row.queryId === Number(queryId)) ?? false

  return (
    <>
      <DetailHeader
        back={
          <Link
            to="/$org/$host/keywords"
            params={{ org, host }}
            search={{ range }}
            className={BACK_LINK}
          >
            <ArrowLeftIcon className="size-3" aria-hidden />
            Keywords
          </Link>
        }
        title={data?.query.text ?? '…'}
        quoted
        actions={
          data && (
            <>
              {starred && (
                <Link
                  to="/$org/$host/search/serp/$queryId"
                  params={{ org, host, queryId }}
                  search={{ range }}
                  className={BACK_LINK}
                >
                  SERP
                </Link>
              )}
              <SerpStar
                site={site}
                host={host}
                queryId={data.query.id}
                keyword={data.query.text}
                tracked={starred}
              />
            </>
          )
        }
      />

      {error ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Keyword not found"
            description={`Nothing in ${host} has been recorded under that keyword. It may belong to another site.`}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/$org/$host/keywords" params={{ org, host }} search={{ range }}>
                  Back to keywords
                </Link>
              </Button>
            }
          />
        </div>
      ) : (
        <SiteSectionBody
          org={org}
          host={host}
          range={range}
          result={data}
          isPending={isPending}
          // The keyword exists or the request 404s, so this page is never the
          // empty one — the dead ends worth keeping are "nothing connected".
          rowCount={1}
          skeleton={<MetricsSkeleton />}
        >
          {data && <KeywordBody data={data} org={org} host={host} queryId={Number(queryId)} />}
        </SiteSectionBody>
      )}
    </>
  )
}

function KeywordBody({
  data,
  org,
  host,
  queryId,
}: {
  data: KeywordDetail
  org: string
  host: string
  queryId: number
}) {
  const { range, site } = useSiteScope()
  const pages = useListExpansion(range, DETAIL_PAGE_SIZE)
  const related = useListExpansion(range, DETAIL_PAGE_SIZE)

  // Both lists arrive with the page; these two only run once someone asks past
  // the first five, and fall back to what the page already holds until they do.
  const pagesPage = useKeywordPages(site, range, queryId, pages.offset, pages.expanded)
  const relatedPage = useKeywordRelated(site, range, queryId, related.offset, related.expanded)

  const pageRows = (pages.expanded ? pagesPage.data?.rows : null) ?? data.pages.rows
  const relatedRows = (related.expanded ? relatedPage.data?.rows : null) ?? data.related.rows

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <MetricCards totals={data.totals} series={data.series} />
        <ShareOfSite part={data.totals.clicks} whole={data.topline.clicks} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DetailList
          title="Landing pages"
          total={data.pages.total}
          expansion={pages}
          isFetching={pagesPage.isFetching}
        >
          <RankedList
            dimension="Page"
            rows={pageRows.map((row) => ({
              key: String(row.pageId),
              label: shortenUrl(row.url, host),
              mono: true,
              metrics: row,
            }))}
            empty="No page-level detail for this keyword in this window. Pairing a keyword with a page is the sparsest report there is, and it is the last one to fill in."
            renderLabel={(row) => (
              <Link
                to="/$org/$host/pages/$pageId"
                params={{ org, host, pageId: row.key }}
                search={{ range }}
                className="underline-offset-2 hover:text-plum hover:underline"
              >
                {row.label}
              </Link>
            )}
          />
        </DetailList>

        {/* Not "similar keywords": nothing here is measuring meaning. These are
            the terms the same pages answer, which is a fact and not a guess. */}
        <DetailList
          title="Keywords sharing these pages"
          total={data.related.total}
          expansion={related}
          isFetching={relatedPage.isFetching}
        >
          <RankedList
            dimension="Keyword"
            metaLabel="Pages"
            rows={relatedRows.map((row) => ({
              key: String(row.queryId),
              label: row.query,
              mono: true,
              meta: row.shared,
              metrics: row,
            }))}
            empty="Nothing else ranks on this keyword's pages in this window."
            renderLabel={(row) => (
              <Link
                to="/$org/$host/keywords/$queryId"
                params={{ org, host, queryId: row.key }}
                search={{ range }}
                className="underline-offset-2 hover:text-plum hover:underline"
              >
                {row.label}
              </Link>
            )}
          />
        </DetailList>
      </div>
    </div>
  )
}
